import { collectionAffirmEnabled, collectionAffirmPrice, initCollectionAffirm } from './affirm';
import '../../../css/modules/collection-affirm.css';
import { signatureCatalogueEnabled, signatureCatalogueCard } from '../signature-catalogue-cards';
import '../../../css/modules/signature-catalogue-cards.css';
import { signatureSansEnabled, signatureWatchCard, signatureWatchOptions, initSignatureCardPreviews } from '../signature-sans-cards';
import '../../../css/modules/signature-sans-cards.css';
import { isProtectedCardProduct } from '../../../../app/Libraries/ProductCardAvailability';
import { atelierRailEnabled, initAtelierRails } from '../atelier-rail';
import { atelierWatchEligible, fetchAtelierWatchGroups, nativeWatchCardOptions } from '../watch-card-options';
import { beginCollectionResults, completeCollectionResults, requestCollectionResultsScroll } from './results-scroll';
import '../../../css/modules/atelier-rail.css';
import { jewelleryCatalogueCard, isNativeJewelleryCatalogueHit } from '../jewellery-card-contract';
import { nativeWatchFacetValue } from '../../../../app/Libraries/NativeWatchFilters';
import { initializeTimepieceFilterJourney } from './timepiece-filter-journey';
import { escapeMarkup, isWatchType, watchCardActions } from '../watch-buying-contract';
import { stardust } from '@eidellev/adonis-stardust/client';
import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import type { BaseHit, Hit, Renderer } from 'instantsearch.js';
import type { HitsRenderState } from 'instantsearch.js/es/connectors/hits/connectHits';
import type { HitsWidgetParams } from 'instantsearch.js/es/widgets/hits/hits';
import { connectSortBy, connectHits, connectInfiniteHits, connectCurrentRefinements, connectSearchBox } from 'instantsearch.js/es/connectors';
import { history } from 'instantsearch.js/es/lib/routers';
import {
  stats,
  clearRefinements,
  rangeSlider,
  pagination,
  configure,
  numericMenu,
} from 'instantsearch.js/es/widgets';
import locationAvailabilityNumericMenu from './widgets/location-inventory-numeric-menu';
import inStockNumericMenu, { IN_STOCK_ATTRIBUTE, stockFilterEnabled as stockFilterEnabledForPage, inStockRouteValue, inStockRouteEnabled } from './widgets/in-stock';
import '../../../css/modules/collection-stock.css';
import $ from 'jquery';
import aa from '../algolia-insights';

import i18next, { type TFunction } from 'i18next';
import i18nextInstance from '../i18next';
let t: TFunction;

import getFilterObjects, {
  Filter,
  getFilterSetIdentifier,
  JEWELLERY_FILTER_SETS,
  JEWELLERY_TYPE_OPEN_SETS,
  STYLE_OPEN_SETS,
} from './frontend-filter-objects';
// Frontend imports can't use app-absolute URLs, so we need to use relative paths
import {
  getIndexName,
  buildSortByOptions,
  getNamedTagsForLocale,
} from '../../../../app/Libraries/Algolia/AlgoliaUtils';
import { trackSearchEvent } from '../search-events';
import { getOptionalFilters } from '../search-personalization';
import { watchRecencyForPage, watchRecencySortOptions, watchRecencyRouteSort, withWatchRecencySearch } from './watch-recency';

import {
  formatAsMoney,
  extractID,
  getSizedShopifyImageUrl,
  getPageLang,
} from '../utils';

const baseSearchClient = algoliasearch(
  process.env.ALGOLIA_APP_ID as string,
  process.env.ALGOLIA_SEARCH_KEY as string,
);

const watchRecency = watchRecencyForPage(document, window.location, getPageLang());
const searchClient = withWatchRecencySearch(baseSearchClient, watchRecency);

const DEFAULT_INDEX = getIndexName(getPageLang(), 'featured', 'asc');

/** Jewellery catalogues swap the watch-specific wording (set by collection.edge) */
const isJewelleryCatalogue = () =>
  document.getElementById('collectionWrapper')?.dataset.catalogueNoun === 'jewellery';

// The Algolia index serves 24 products per page (8 complete rows of 3 on
// desktop, 12 pairs of 2 on mobile). Collection Break tiles that leave a
// desktop row incomplete are compensated with filler cards on that page
// only — see fetchBreakFillers.

// This allows us to cancel a hits render if a new one is triggered before the last one finishes
let latestCallTimestamp = new Date().getTime();

/**
 * When a Collection Break tile spanning 1-2 of the 3 desktop columns lands on
 * the current page, the page's last desktop row would end incomplete. Fetch
 * the first 1-2 products of the next page as desktop-only fillers so the rows
 * still end complete. Pagination stays a uniform 24 per page — the fillers
 * simply repeat as the leading products of the next page, and are hidden below
 * the desktop breakpoint where the break spans the full row anyway.
 */
const fetchBreakFillers = async (hits: Hit[], results: any): Promise<any[]> => {
  if (!results) return [];

  // Columns taken up by the break tiles landing on this page — a break lands
  // wherever its data-after global position is showing (see injectCollectionBreaks)
  const breakColumnsTotal = [
    ...document.querySelectorAll<HTMLTemplateElement>('template.js-collection-break'),
  ].reduce((sum, template) => {
    const after = parseInt(template.dataset.after || '', 10);
    if (!after || !hits.some((hit) => hit.__position === after)) return sum;
    return sum + Math.min(Math.max(parseInt(template.dataset.columns || '3', 10) || 3, 1), 3);
  }, 0);

  const missing = (3 - (breakColumnsTotal % 3)) % 3;
  if (!missing) return [];

  try {
    const raw = results._rawResults?.[0];
    const offset = (results.page + 1) * results.hitsPerPage;
    const length = Math.min(missing, Math.max((results.nbHits ?? 0) - offset, 0));
    if (!raw || length < 1) return [];

    // Re-run the exact same query, but only for the products just past this page
    const params = Object.fromEntries(new URLSearchParams(raw.params));
    delete params.page;
    delete params.hitsPerPage;

    const { results: [res] } = await searchClient.search([
      { indexName: raw.index, params: { ...params, offset, length } as any },
    ]);

    return ((res as any)?.hits || []).slice(0, missing).map((hit: any, i: number) => ({
      ...hit,
      __position: offset + i + 1,
      __queryID: (res as any)?.queryID,
    }));
  } catch {
    return []; // fillers are cosmetic — without them the row just ends short
  }
};

/**
 * Inject Contentful "Collection Break" merchandising tiles into the hits grid.
 * Each break is rendered server-side as a <template class="js-collection-break">
 * with data-after (the global product position it follows) and data-columns
 * (how many of the 3 desktop columns it spans — mobile always spans the row).
 */
const injectCollectionBreaks = (container: HTMLElement) => {
  const templates = document.querySelectorAll<HTMLTemplateElement>('template.js-collection-break');
  if (!templates.length) return;

  const grid = container.querySelector<HTMLElement>('.collection__grid');
  if (!grid) return;

  templates.forEach((template) => {
    const after = parseInt(template.dataset.after || '', 10);
    if (!after) return;

    const columns = Math.min(Math.max(parseInt(template.dataset.columns || '3', 10) || 3, 1), 3);

    // __position is the global 1-based position across pagination, so the
    // break lands on whichever page shows that product
    const insertAt = (position: number, className: string) => {
      // Fillers duplicate the next page's leading products, so skip them here
      const target = [...grid.querySelectorAll(`.js-product[data-position="${position}"]`)]
        .map((el) => el.closest('.collection__grid-item'))
        .find((item) => item && !item.classList.contains('collection__grid-item--desktop-extra'));
      if (!target) return false;

      const breakItem = document.createElement('li');
      breakItem.className =
        `collection__grid-item collection__grid-item--break collection__grid-item--break-cols-${columns}` +
        (className ? ` ${className}` : '');
      breakItem.appendChild(template.content.cloneNode(true));
      target.after(breakItem);
      return true;
    };

    // Mobile rows are pairs, so an odd position would leave a lone product —
    // shift the mobile copy one product later to keep the pairs intact
    const afterMobile = after % 2 ? after + 1 : after;

    if (afterMobile === after) {
      insertAt(after, '');
    } else {
      insertAt(after, 'collection__grid-item--break-desktop');
      // Fall back to the desktop position if the next product isn't on this page
      if (!insertAt(afterMobile, 'collection__grid-item--break-mobile')) {
        insertAt(after, 'collection__grid-item--break-mobile');
      }
    }
  });

  markGridRowEnds(grid);
};

/**
 * With a break injected the nth-of-type border rules no longer line up, so mark
 * the last item of each visual row for both the 2-col (below @md) and 3-col
 * (@md and up) layouts and let the CSS use the classes instead.
 */
const markGridRowEnds = (grid: HTMLElement) => {
  if (!grid.querySelector('.collection__grid-item--break')) return;

  grid.classList.add('collection__grid--has-breaks');

  const items = Array.from(grid.children) as HTMLElement[];

  [
    { rowWidth: 2, className: 'collection__grid-item--m-row-end' },
    { rowWidth: 3, className: 'collection__grid-item--d-row-end' },
  ].forEach(({ rowWidth, className }) => {
    let col = 0;
    let prev: HTMLElement | null = null;

    // Skip the break copy that's hidden at this breakpoint (and the extra
    // row-fill products, which only render at desktop widths)
    const visibleItems = items.filter((item) =>
      rowWidth === 2
        ? !item.classList.contains('collection__grid-item--break-desktop')
          && !item.classList.contains('collection__grid-item--desktop-extra')
        : !item.classList.contains('collection__grid-item--break-mobile')
    );

    visibleItems.forEach((item) => {
      let span = 1;
      if (item.classList.contains('collection__grid-item--break')) {
        span = rowWidth === 2
          ? rowWidth // always a full row below the desktop breakpoint
          : Math.min(parseInt(item.className.match(/break-cols-(\d)/)?.[1] || '3', 10), rowWidth);
      }

      // Wraps onto a new row, so the previous item ends its (incomplete) row
      if (col + span > rowWidth) {
        prev?.classList.add(className);
        col = 0;
      }

      col += span;

      if (col >= rowWidth) {
        item.classList.add(className);
        col = 0;
      }

      prev = item;
    });
  });
};

// Title-swatch grouping helpers shared with the product page module
// (product/title-swatches.ts) — see title-swatch-utils for the rules
import {
  getInternalTitle,
  getGroupTitle,
  getGroupKey,
  isSwatchHit,
  isInStock,
} from '../title-swatch-utils';

/**
 * Whether the add-to-cart button should show for a hit — not available online,
 * out of stock, or needing a size/other selection all disable it
 */
const isHitAvailable = (hit: Hit<BaseHit>) => {
  // NOTE: Using original (non-locale) named tags because this one isn't prefixed
  let available = hit.named_tags.available
    ? hit.inventory_available
      ? true
      : false
    : false;

  if (
    hit.option_names.length > 1 ||
    hit.option_names.some((e) => e.includes('size')) ||
    hit.tags?.includes('resizeable')
  ) {
    available = false;
  }

  return available;
};

/** Build the product card price markup (hidden price / compare-at / default) */
const buildPriceMarkup = (opts: { hidePrice: boolean; price: number; compareAtPrice?: number | null }) => {
  if (opts.hidePrice) {
    return `<p class="product-card__price product-card__price--hidden">${t('general:priceRequest')}</p>`;
  }

  if (opts.compareAtPrice && opts.price !== opts.compareAtPrice) {
    return `
      <p class="product-card__price js-product-price">
        <strike>${formatAsMoney(opts.compareAtPrice, getPageLang())}</strike>
        <span>${formatAsMoney(opts.price, getPageLang())}</span>
      </p>`;
  }

  return `
    <p class="product-card__price js-product-price">
      ${formatAsMoney(opts.price, getPageLang())}
    </p>`;
};

// Cache of group key -> sibling products so re-renders don't re-query Algolia
const titleSiblingsCache = new Map<string, any[]>();

/**
 * Fetch every product in the index in each hit's colourway group — siblings
 * can be on other result pages, so the current page's hits aren't enough.
 * Internal-title groups match on the meta.custom.title_internal facet;
 * fallback groups (blank metafield) match on the visible title text. One
 * multi-query request covers all uncached groups.
 */
const fetchTitleSiblings = async (groupHits: any[]) => {
  const byKey = new Map<string, any>();
  groupHits.forEach((groupHit) => {
    const key = getGroupKey(groupHit);
    if (!titleSiblingsCache.has(key) && !byKey.has(key)) byKey.set(key, groupHit);
  });
  const uncached = [...byKey.entries()];
  if (!uncached.length) return;

  const RETRIEVE = [
    'title', 'handle', 'image', 'price', 'compare_at_price', 'meta',
    'tags', 'named_tags', 'inventory_available', 'option_names', 'product_type',
  ];

  const { results } = await searchClient.search(
    uncached.map(([key, groupHit]) => key.startsWith('m:')
      ? {
        indexName: DEFAULT_INDEX,
        query: '',
        params: {
          hitsPerPage: 50,
          facetFilters: [[`meta.custom.title_internal:${getInternalTitle(groupHit)}`]],
          attributesToRetrieve: RETRIEVE,
          analytics: false,
          clickAnalytics: false,
        },
      }
      : {
        indexName: DEFAULT_INDEX,
        query: String(groupHit.title || ''),
        params: {
          hitsPerPage: 50,
          restrictSearchableAttributes: ['title'],
          attributesToRetrieve: RETRIEVE,
          analytics: false,
          clickAnalytics: false,
        },
      }) as any
  );

  results.forEach((result, index) => {
    const [key] = uncached[index];
    // Exact matches within the same group kind, in stock and available online.
    // Fallback (title) groups exclude products that HAVE an internal title —
    // those belong to their curated group only.
    const siblings = ((result as any).hits || [])
      .filter((sibling: any) => getGroupKey(sibling) === key && isInStock(sibling));
    titleSiblingsCache.set(key, siblings);
  });
};

/** Show the swatch carousel arrows only where the row actually overflows */
const markScrollableTitleSwatches = (container: HTMLElement) => {
  container.querySelectorAll<HTMLElement>('.title-swatches').forEach((el) => {
    const track = el.querySelector<HTMLElement>('.js-title-swatches-track');
    if (!track) return;
    el.classList.toggle('title-swatches--scrollable', track.scrollWidth > track.clientWidth + 1);
  });
};

/**
 * Make async calls and render the hits once they've been loaded
 * @param hits The hits from the Algolia renderHits function
 * @param container The container to render the hits into
 * @param timestamp The timestamp for when this call was made. Used for skipping render if out of date.
 */
const loadHitsAsync = async (hits: Hit<BaseHit>[] & Hit[], container: HTMLElement, timestamp: number, results?: any, resultsRevision?: number) => {
  // Editorial cards hide the secondary hover image and the per-variant colour
  // dots, so the per-card image/colour API fetches are pure waste there — skip
  // them and card-building stays synchronous (no 24-way fetch fan-out).
  const editorial = !!document.querySelector('.collection-page--editorial');
  const exactWatchCards = !!document.querySelector('[data-watch-buying="collection"]');
  const signatureCatalogue = signatureCatalogueEnabled();
  const collectionFinance = collectionAffirmEnabled();
  const isSignatureCatalogueHit = (hit: any) => signatureCatalogue && !isProtectedCardProduct(hit, window.location.pathname);
  const isExactWatch = (hit: any) => exactWatchCards && isWatchType(hit.product_type) && !/rolex/i.test(String(hit.named_tags?.brand || hit.vendor || ''));
  const exactJewelleryCards = !!document.querySelector('[data-jewellery-buying="collection"]');
  // Explicit card locale survives a stale preference cookie on the product page.
  // Accessory catalogues retain their existing layout and supported-host scope.
  const accessoryProductLinks = ['www.raffi-jewellers.ca', 'raffi-jewellers.ca', 'raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com'].includes(window.location.hostname)
    && /^\/(?:fr\/)?collections\/gifts-accessories(?:\/[a-z0-9-]+)*\/?$/i.test(window.location.pathname);
  const localizedProductLinks = (exactWatchCards || exactJewelleryCards || accessoryProductLinks || signatureCatalogue)
    && ['en', 'fr'].includes(getPageLang());
  // Locale applies to inquiry-only items too; their purchase policy stays unchanged.
  const keepProductLocale = (hit: any) => localizedProductLinks && !isProtectedCardProduct({
    brand: hit.brand, vendor: hit.vendor, named_tags: hit.named_tags, handle: hit.handle,
  }, window.location.pathname);
  const isExactJewellery = (hit: any) => exactJewelleryCards && isNativeJewelleryCatalogueHit(hit, window.location.pathname);
  // Native watch collections can also contain cufflinks, straps and accessories.
  const isExactPiece = (hit: any) => ((exactWatchCards && !isWatchType(hit.product_type)) || exactJewelleryCards)
    && isNativeJewelleryCatalogueHit(hit, window.location.pathname);
  const isExactCard = (hit: any) => isExactWatch(hit) || isExactPiece(hit);
  // Desktop-only fillers appended when a Collection Break on this page leaves
  // the last desktop row incomplete (see fetchBreakFillers)
  const fillerHits = exactWatchCards || exactJewelleryCards || signatureCatalogue ? [] : await fetchBreakFillers(hits, results);
  const allHits = [...hits, ...fillerHits];

  // Group products by internal title (custom.title_internal metafield, falling
  // back to the visible title when blank) so each card can show its sibling
  // colourways (separate Shopify products, e.g. one Seamaster per strap/dial
  // colour). Curated groups (any product type) + watches; siblings come from the whole index, not just this page.
  const watchHits = allHits.filter((h) => !isExactCard(h) && isSwatchHit(h) && getGroupTitle(h));
  const titleGroups = new Map<string, any[]>();

  try {
    await fetchTitleSiblings(watchHits);
    watchHits.forEach((groupHit) => {
      const key = getGroupKey(groupHit);
      titleGroups.set(key, titleSiblingsCache.get(key) || []);
    });
  } catch {
    // Algolia lookup failed — fall back to grouping just this page's hits
    allHits.forEach((groupHit) => {
      if (isExactCard(groupHit) || !isInStock(groupHit) || !getGroupTitle(groupHit)) return;
      const key = getGroupKey(groupHit);
      if (!titleGroups.has(key)) titleGroups.set(key, []);
      titleGroups.get(key)!.push(groupHit);
    });
  }
  const atelierActive = exactWatchCards && atelierRailEnabled();
  const atelierGroups = atelierActive
    ? await fetchAtelierWatchGroups(allHits.filter(isExactWatch), queries => searchClient.search(queries), DEFAULT_INDEX)
    : new Map<string, any[]>();
  // console.debug(`🚀 ~ loadHitsAsync (@${timestamp}) start`);
  const hitToCard = async (hit: any, extraClass = '') => {
      const exactWatch = isExactWatch(hit);
      const exactJewellery = isExactJewellery(hit);
      const exactPiece = isExactPiece(hit);
      const exactCard = exactWatch || exactPiece || isSignatureCatalogueHit(hit);
      const atelierCard = atelierActive && exactWatch && atelierWatchEligible(hit);
      let classes: string = '';
      let images: any[] = [];

      const localeNamedTags = getNamedTagsForLocale(hit.named_tags, getPageLang());
      // console.debug('🚀 ~ loadHitsAsync ~ localeNamedTags:', localeNamedTags);

      images.push({
        // Default image
        url: hit.image
          ? getSizedShopifyImageUrl(hit.image, 768)
          : '/img/placeholder.png',
        srcSet: hit.image
          ? `${getSizedShopifyImageUrl(hit.image, 768)} 767w, ${getSizedShopifyImageUrl(hit.image, 1200)} 1200w`
          : '/img/placeholder.png',
        class: 'primary',
      });

      // The shared category card needs no secondary image or colour lookup.
      // Leave protected hits on their existing renderer and purchase controls.
      if (isSignatureCatalogueHit(hit) && !exactWatch) {
        const query: Record<string, string> = { v: String(hit.objectID), oid: String(hit.objectID), lang: getPageLang() };
        if (hit.__queryID) query.qid = hit.__queryID;
        const url = stardust.route(`${getPageLang()}.shopify.slug`, { slug: hit.handle }, { qs: query });
        const price = buildPriceMarkup({ hidePrice: !!hit.tags?.includes('hide-price'), price: hit.price, compareAtPrice: hit.compare_at_price });
        return signatureCatalogueCard(hit, url, images[0], collectionAffirmPrice(hit, price, window.location.pathname, collectionFinance), getPageLang(), {
          view: t('general:signatureCards.viewProduct'), add: t('general:watchJourney.addToBag'),
          inquire: t('general:signatureCards.inquireNow'), options: t('general:signatureCards.chooseOptions'),
          appointment: t('general:signatureCards.bookAppointment'),
          inStoreOnly: t('general:signatureCards.inStoreOnly'), outOfStock: t('general:signatureCards.outOfStock'),
        }, extraClass, window.location.pathname);
      }

      // Editorial cards don't use the hover/secondary image, so skip the fetch
      if (!editorial && !exactCard) {
        // Make a call to get the images
        const productImages = await fetch(
          stardust.route('api.shop.images', { id: hit.id }, { qs: { lang: getPageLang() } }),
          { signal: AbortSignal.timeout(3000) }
        ).then((r) => r.ok ? r.json() : []).catch(() => []);

        if (productImages.length > 1) {
          images.push({
            url: productImages[1].thumbnail,
            srcSet: `${productImages[1].thumbnail} 767w, ${productImages[1].url} 1200w`,
            class: 'secondary',
          });

          classes += 'product-card--multi-image';

          if (hit.tags.includes('lifestyle-hover')) {
            classes += ' product-card--lifestyle-image';
          }
        }
      }

      let imageHTML = images
        .map((image) => {
          return `<img class="product-card__img product-card__img--${image.class} lazyload"
          data-src="${image.url}" data-srcset="${image.srcSet}" />`;
        })
        .join('');

      let imageButtons = '';

      if (images.length > 1) {
        imageButtons =
          `<button class="product-card__img-button product-card__img-button--prev js-prev-img" aria-label="${t('general:previousImage')}" disabled></button>
          <button class="product-card__img-button product-card__img-button--next js-next-img" aria-label="${t('general:nextImage')}"></button>`;
      }

      let badgeHTML = '';
      let buttonHTML = '';

      let available = isHitAvailable(hit);

      if (localeNamedTags.newArrival) {
        badgeHTML =
          `<span class="product-card__badge product-card__badge--new">${t('general:new')}</span>`;
      }

      // Editorial tiles are beige — the sand bag circle disappears on them
      const bagClasses = editorial ? 'bag-btn bag-btn--dark' : 'bag-btn';

      if (hit.option_names.length > 1) {
        buttonHTML = '';
      } else if (available) {
        buttonHTML = `
          <button class="${bagClasses} product-card__add-to-cart js-add-to-cart" data-id="gid://shopify/ProductVariant/${hit.objectID}">
            <span class="sr-only">${t('general:addToCart')}</span>
          </button>`;
      } else {
        buttonHTML = `
          <button class="${bagClasses} product-card__add-to-cart js-add-to-cart hidden" disabled data-id="gid://shopify/ProductVariant/${hit.objectID}">
            <span class="sr-only">${t('general:addToCart')}</span>
          </button>`;
      }

      // variants should only show for colour options, not size etc.
      // We're showing 3, and if there are more than that the plus button shows
      let variantsHTML = '';

      // Check if there are colour or material variants for this item.
      // Editorial cards hide the mini variant dots, so skip the colours fetch.
      if (
        !editorial && !exactCard && (
          hit.option_names.includes('color') ||
          hit.option_names.includes('colour') ||
          hit.option_names.includes('material')
        )
      ) {
        // Make a call to get the variants
        const variantColours = await fetch(
          stardust.route('api.shop.colours', { id: hit.id }, { qs: { lang: getPageLang() } }),
          { signal: AbortSignal.timeout(3000) }
        ).then((r) => r.ok ? r.json() : []).catch(() => []);

        if (variantColours.length > 1) {
          let html = variantColours
            .map((variant) => {
              let checked =
                Object.values(hit.options).indexOf(variant.value) === 0
                  ? 'checked'
                  : '';

              // we don't want to actually disable, but simulate instead
              let disabled = variant.inStock ? '' : 'disabled';
              // Don't set imageset attr if no variant image
              const imageSet = variant.image
                ? `${getSizedShopifyImageUrl(variant.image, 768)} 767w, ${getSizedShopifyImageUrl(variant.image, 1200)} 1200w`
                : '';
              // let disabled = '';
              return `
              <label class="variant-selectors__option variant-selectors__option--color
                variant-selectors__option--${variant.classes} js-mini-variant-selector"
              >
                <input class="variant-selectors__input ${disabled}" type="radio" name="${hit.handle}-option-${variant.name}"
                  value="${variant.value}" data-option="${variant.name}" data-product="${hit.handle}" data-imageset="${imageSet}"
                  data-price="${formatAsMoney(variant.price)}"
                  data-compare-at-price="${variant.compareAtPrice ? formatAsMoney(variant.compareAtPrice) : ''}"
                  data-variant="${variant.id}"
                  ${checked}
                />
                <span class="variant-selectors__option-btn" title="${variant.value}"></span>
                <span class="variant-selectors__option-strikethrough"></span>
                <span class="variant-selectors__option-underline"></span>
              </label>`;
            })
            .slice(0, 4)
            .join('');

          if (variantColours.length > 3) {
            html += '<span class="variant-selectors__additional">+</span>';
          }

          variantsHTML = `
            <div role="group" aria-labelledby="option-label_colour" class="variant-selectors variant-selectors--mini">
              <span class="sr-only" id="option-label__colour">${t('general:selectColour')}</span>
              <div class="variant-selectors__buttons">
                ${html}
              </div>
            </div>`;
        }
      }

      // Add to cart button needs to be conditional, should not show if it's not
      // available online, out of stock, or if there are more than just colour
      // selector options (i.e., if they need to choose a size or something, should not show)
      /****** If you make ANY EDITS to this you need to also update the ProductCard component file as well ******/

      const priceMarkup = buildPriceMarkup({
        hidePrice: !!hit.tags?.includes('hide-price'),
        price: hit.price,
        compareAtPrice: hit.compare_at_price,
      });

      // Colour swatches linking the sibling products in this card's colourway
      // group (internal title, falling back to visible title) — clicking one
      // swaps the card's image/price/link in place (Nike-style). Watches /
      // Timepieces only. Products without siblings still render an empty
      // placeholder strip so titles align across rows.
      let titleSwatchesHTML = exactCard ? '' : '<div class="title-swatches title-swatches--empty"></div>';
      let titleGroup = !exactCard && isSwatchHit(hit) && getGroupTitle(hit)
        ? titleGroups.get(getGroupKey(hit)) || []
        : [];

      // Make sure the current product is always in its own group
      if (titleGroup.length && !titleGroup.some((s) => String(s.objectID) === String(hit.objectID))) {
        titleGroup = [hit, ...titleGroup];
      }

      if (titleGroup.length > 1) {
        // The card's own product always leads the swatch row (copy — the
        // group array is shared via titleSiblingsCache, don't mutate it)
        const orderedGroup = [
          ...titleGroup.filter((s) => String(s.objectID) === String(hit.objectID)),
          ...titleGroup.filter((s) => String(s.objectID) !== String(hit.objectID)),
        ];
        const swatches = orderedGroup
          .map((sibling) => {
            const siblingUrl = stardust.route(`${getPageLang()}.shopify.slug`, {
              slug: sibling.handle,
            }, { qs: { v: sibling.objectID, oid: sibling.objectID,
              ...(keepProductLocale(sibling) ? { lang: getPageLang() } : {}),
            } });

            const selected = sibling.objectID === hit.objectID
              ? ' title-swatches__swatch--selected'
              : '';

            const thumb = sibling.image
              ? getSizedShopifyImageUrl(sibling.image, 200)
              : '/img/placeholder.png';
            const cardImage = sibling.image
              ? getSizedShopifyImageUrl(sibling.image, 768)
              : '/img/placeholder.png';
            const cardSrcSet = sibling.image
              ? `${getSizedShopifyImageUrl(sibling.image, 768)} 767w, ${getSizedShopifyImageUrl(sibling.image, 1200)} 1200w`
              : '';

            return `
              <button type="button" class="title-swatches__swatch js-title-swatch${selected}"
                data-url="${siblingUrl}"
                data-handle="${sibling.handle}"
                data-object-id="${sibling.objectID}"
                data-image="${cardImage}"
                data-srcset="${cardSrcSet}"
                data-price="${sibling.price}"
                data-compare-at-price="${sibling.compare_at_price || ''}"
                data-hide-price="${sibling.tags?.includes('hide-price') ? 'true' : 'false'}"
                data-available="${isHitAvailable(sibling) ? 'true' : 'false'}"
                aria-label="${hit.title}"
              >
                <img src="${thumb}" alt="" loading="lazy" />
              </button>`;
          })
          .join('');

        titleSwatchesHTML = `
          <div class="title-swatches">
            <button type="button" class="title-swatches__arrow title-swatches__arrow--prev js-title-swatches-prev" aria-label="${t('general:previousImage')}"></button>
            <div class="title-swatches__track js-title-swatches-track">${swatches}</div>
            <button type="button" class="title-swatches__arrow title-swatches__arrow--next js-title-swatches-next" aria-label="${t('general:nextImage')}"></button>
          </div>`;
      }

      const productUrlQueryString = {
        v: hit.objectID,
        oid: hit.objectID,
        ...(keepProductLocale(hit) ? { lang: getPageLang() } : {}),
      };
      if (hit.__queryID) {
        productUrlQueryString['qid'] = hit.__queryID;
      }
      const productUrl = stardust.route(`${getPageLang()}.shopify.slug`, {
        slug: hit.handle,
      }, { qs: productUrlQueryString });

      if (exactPiece) {
        return jewelleryCatalogueCard(hit, productUrl, images[0], priceMarkup, getPageLang(), {
          shopNow: t('general:watchJourney.shopNow'),
          inquireAvailability: t('general:watchJourney.inquireAvailability'),
          tryOn: t('jewelleryTryOn:tryOn'),
          comingSoon: t('jewelleryTryOn:comingSoon'),
        }, extraClass, window.location.pathname, exactJewellery);
      }

      // Editorial collections (.collection-page--editorial): brand line above
      // a shortened name, a derived "NN mm" spec line, and an outlined shop
      // button (the regular card buttons are hidden by CSS). `editorial` is
      // computed once at the top of loadHitsAsync.
      let brandLineHTML = '';
      let specLineHTML = '';
      let shopButtonHTML = '';
      let cardTitle = hit.title;
      let nativeWatchSize = '';
      if (editorial || exactWatch) {
        // Prefer the curated brand named-tag; fall back to the Shopify vendor
        // so products missing the brand tag (e.g. some Tacori pieces) still
        // show a brand line and get their name aligned/de-prefixed.
        const brand = (hit.named_tags && hit.named_tags.brand) || hit.vendor || '';
        if (brand) {
          brandLineHTML = `<p class="product-card__brand-line">${brand}</p>`;
          if (cardTitle.toLowerCase().startsWith(brand.toLowerCase())) {
            cardTitle = cardTitle.slice(brand.length).trim();
          }
        }
        // spec = case size · movement tag; always rendered so prices/buttons
        // align across the row. Case size comes from the public title, the
        // internal title ("TUDOR Clair de Rose 34mm"), or — when a title
        // carries the size as a bare model number ("Superocean Automatic 36",
        // "Classic AVI Chronograph 42 Mosquito") — a standalone 20–50 number.
        // The Key Features metafield holding Case Size isn't in the search
        // index, so titles are the only source.
        const internalTitle = (hit.meta && hit.meta.custom && hit.meta.custom.title_internal) || '';
        const bareSize = (text: string) => {
          const match = /(?:^|\s)(\d{2})(?:\s|$)/.exec(text);
          const n = match ? Number(match[1]) : 0;
          return n >= 20 && n <= 50 ? match : null;
        };
        const mm = exactWatch ? (
          /(?:^|[^A-Za-z0-9.,])(\d{2}(?:[.,]\d+)?)\s?mm\b/i.exec(hit.title || '')
          || /(?:^|[^A-Za-z0-9.,])(\d{2}(?:[.,]\d+)?)\s?mm\b/i.exec(internalTitle)
          || bareSize(hit.title || '')
          || bareSize(internalTitle)
          // description often reads "41mm steel case" or "model in 43mm"
          // (keyword-context match so strap/bracelet widths don't leak in)
          || /(?:^|[^A-Za-z0-9.,])(\d{2}(?:[.,]\d+)?)\s?mm\b[^.]{0,25}?(?:case|diameter)/i.exec(hit.body_html_safe || '')
          || /(?:case|model|diameter)[^.]{0,20}?(?:^|[^A-Za-z0-9.,])(\d{2}(?:[.,]\d+)?)\s?mm\b/i.exec(hit.body_html_safe || '')
        ) : (
          /(\d{2}(?:\.\d)?)\s?mm/i.exec(hit.title || '')
          || /(\d{2}(?:\.\d)?)\s?mm/i.exec(internalTitle)
          || bareSize(hit.title || '')
          || bareSize(internalTitle)
          // description often reads "41mm steel case" or "model in 43mm"
          // (keyword-context match so strap/bracelet widths don't leak in)
          || /(\d{2}(?:\.\d)?)\s?mm[^.]{0,25}?(?:case|diameter)/i.exec(hit.body_html_safe || '')
          || /(?:case|model|diameter)[^.]{0,20}?(\d{2}(?:\.\d)?)\s?mm/i.exec(hit.body_html_safe || '')
        );
        if (exactWatch && mm) nativeWatchSize = `${mm[1]} mm`;
        const tagPrefix = getPageLang() === 'fr' ? 'FR_' : 'EN_';
        const movementTag = hit.named_tags && (hit.named_tags[`${tagPrefix}movement`] || hit.named_tags.movement);
        const movement = Array.isArray(movementTag) ? movementTag[0] : movementTag;
        const spec = [mm ? `${mm[1]} mm` : '', movement || ''].filter(Boolean).join(' · ');
        specLineHTML = `<p class="product-card__spec-line">${spec || '&nbsp;'}</p>`;
        shopButtonHTML = `<a href="${productUrl}" class="product-card__shop-editorial">${t('general:shopWatch')}</a>`;
      }

      if (exactWatch && signatureSansEnabled() && !isProtectedCardProduct(hit, window.location.pathname, productUrl)) {
        const labels = {
          view: t('general:signatureCards.viewWatch'), add: t('general:watchJourney.addToBag'),
          inquire: t('general:signatureCards.inquireNow'), options: t('general:signatureCards.chooseOptions'),
          appointment: t('general:signatureCards.bookAppointment'),
          inStoreOnly: t('general:signatureCards.inStoreOnly'), outOfStock: t('general:signatureCards.outOfStock'),
        };
        return signatureWatchCard(hit, productUrl, images[0], collectionAffirmPrice(hit, priceMarkup, window.location.pathname, collectionFinance),
          signatureWatchOptions(hit, atelierGroups.get(getGroupKey(hit)) || [], getPageLang(), labels,
            image => getSizedShopifyImageUrl(image, 200) || image,
            item => collectionAffirmPrice(item, buildPriceMarkup({ hidePrice: item.tags?.includes('hide-price'), price: item.price, compareAtPrice: item.compare_at_price }), window.location.pathname, collectionFinance),
            image => ({ url: getSizedShopifyImageUrl(image, 768) || image, srcSet: `${getSizedShopifyImageUrl(image, 768)} 767w, ${getSizedShopifyImageUrl(image, 1200)} 1200w` })),
          nativeWatchSize, getPageLang(), labels, window.location.pathname, extraClass);
      }

      if (exactWatch) {
        buttonHTML = ''; variantsHTML = '';
        shopButtonHTML = watchCardActions({
          id: `${hit.handle}:${hit.objectID}`, title: hit.title,
          handle: hit.handle, tags: hit.tags, inventory_available: hit.inventory_available, inventory_quantity: hit.inventory_quantity,
          brand: hit.named_tags?.brand || hit.vendor || '', sku: hit.sku || '',
          image: hit.image ? getSizedShopifyImageUrl(hit.image, 1200) : '',
        }, productUrl, { shopNow:t('general:watchJourney.shopNow'), inquireAvailability:t('general:watchJourney.inquireAvailability'), tryOn:t('general:watchJourney.tryOn') }, window.location.pathname);
        // Match the approved shopping-card composition while retaining the native
        // product/variant URL and the existing try-on contract for this exact hit.
        const e = escapeMarkup;
        const reference = String(hit.sku || '').trim();
        return `
        <li class="collection__grid-item rf-native-watch-item${extraClass}">
          <article class="product-card rf-native-watch-card js-product" data-watch-card${atelierCard ? ' data-atelier-card' : ''}
            data-product="${e(hit.handle)}" data-position="${e(hit.__position)}"
            data-query-id="${e(hit.__queryID)}" data-object-id="${e(hit.objectID)}">
            <a href="${e(productUrl)}" class="product-card__images rf-native-watch-photo" tabindex="-1" aria-hidden="true">
              <img class="product-card__img product-card__img--primary lazyload" alt=""
                data-src="${e(images[0].url)}" data-srcset="${e(images[0].srcSet)}" decoding="async" />
            </a>
            <div class="product-card__details rf-native-watch-body">
              <p class="product-card__brand-line">${e(hit.named_tags?.brand || hit.vendor || '')}</p>
              <h3 class="product-card__title"><a href="${e(productUrl)}">${e(cardTitle)}</a></h3>
              ${atelierCard ? '<div class="rf-atelier-meta">' : ''}
              <p class="product-card__spec-line rf-native-watch-meta">
                ${nativeWatchSize ? `<span>${e(nativeWatchSize)}</span>` : ''}
                ${reference ? `<span class="rf-native-watch-reference">${getPageLang() === 'fr' ? 'Réf.' : 'Ref.'} ${e(reference)}</span>` : ''}
              </p>
              ${atelierCard ? nativeWatchCardOptions(hit, atelierGroups.get(getGroupKey(hit)) || [], getPageLang(), image => getSizedShopifyImageUrl(image, 200) || image) + '</div>' : ''}
              ${priceMarkup}
              ${shopButtonHTML}
            </div>
          </article>
        </li>`;
      }

      return `
      <li class="collection__grid-item${extraClass}">
        <div class="product-card ${classes} js-product"
          data-product="${hit.handle}"
          data-position="${hit.__position}"
          data-query-id="${hit.__queryID}"
          data-object-id="${hit.objectID}"
          ${exactWatch ? 'data-watch-card' : ''}
        >
          <a href="${productUrl}" class="product-card__images" tabindex="-1">
            ${imageHTML}
          </a>

          ${badgeHTML}

          <div class="product-card__details">
            ${titleSwatchesHTML}
            ${brandLineHTML}
            <h3 class="product-card__title">
              <a href="${productUrl}">${cardTitle}</a>
            </h3>
            ${specLineHTML}
            ${priceMarkup}
            ${shopButtonHTML}
          </div>

          ${imageButtons}

          <div class="product-card__btns">
            ${buttonHTML}
            ${variantsHTML}
          </div>

        </div>
      </li>`;
  };

  const innerHtml = await Promise.all([
    ...hits.map((hit) => hitToCard(hit)),
    ...fillerHits.map((hit) => hitToCard(hit, ' collection__grid-item--desktop-extra')),
  ]);

  // If this call's timestamp is not out of date, update the UI
  if (latestCallTimestamp <= timestamp) {
    // console.debug(`🚀 ~ loadHitsAsync (@${timestamp}) updating`);
    container.innerHTML = `
      <ol class="collection__grid">
        ${innerHtml.join('')}
      </ol>`;
    injectCollectionBreaks(container);
    markScrollableTitleSwatches(container);
    initAtelierRails(container);
    initSignatureCardPreviews();
    initCollectionAffirm();
    completeCollectionResults(container, resultsRevision);
  // } else {
  //   console.debug(`🚀 ~ loadHitsAsync (@${timestamp}) cancelled and skipping`);
  }
};

/**
 * The renderHits function for the custom hits widget.
 * It sets the initial state to show a loading spinner,
 * and calls the loadHitsAsync function to render the hits.
 * It will abort the render if showing the "no hits" message.
 */
const renderHits: Renderer<HitsRenderState, HitsWidgetParams> = (renderOptions, isFirstRender) => {
  const { hits, widgetParams, results } = renderOptions;
  // console.debug('🚀 ~ renderHits ~ hits:', hits);

  const container = typeof widgetParams.container === 'string'
    ? document.querySelector<HTMLElement>(widgetParams.container)
    : widgetParams.container;

  if (!container) {
    console.error('Hits widget: container not found');
    return;
  }

  // When renderHits is called, we update the latest timestamp
  latestCallTimestamp = new Date().getTime();

  if (isFirstRender) {
    // for some reason I can only get this to work properly if its run on first render, using jquery...
    // - otherwise click event fires 3 times?
    bindHitEventListeners();
    return;
  }

  const resultsRevision = beginCollectionResults(container);

  if (!hits.length) {
    container.innerHTML = `
      <ol class="collection__grid collection__grid--empty">
        <li class="collection__grid-item collection__grid-item--empty">
          <p class="collection__empty-msg">${t('general:noMatchingResults')}</p>
        </li>
      </ol>`;
    // If we hit here, we need to bump the timestamp to stop any in-progress `loadHits()` calls
    // from replacing the content
    // console.debug('🚀 ~ renderHits ~ cancelling)');
    latestCallTimestamp = new Date().getTime();
    completeCollectionResults(container, resultsRevision);
    return;
  }

  // Show loading spinner
  // We put placeholder grid items there so that the page height doesn't jump around
  // TODO: It would be nicer to show skeleton loading cards instead, but this works for a quick fix
  // https://www.freecodecamp.org/news/how-to-build-skeleton-screens-using-css-for-better-user-experience/
  container.innerHTML = `
    <ol class="collection__grid collection__grid--loading">
      ${
        `<li class="collection__grid-item">
          <div class="pagespinner pagespinner--small spin"></div>
        </li>`.repeat(hits.length)
      }
    </ol>`;

  // NOTE: This is async but we just call it so it updates the UI when ready
  // console.debug(`🚀 ~ renderHits ~ calling loadHitsAsync (@${latestCallTimestamp})`);
  loadHitsAsync(hits, container, latestCallTimestamp, results, resultsRevision);

  function bindHitEventListeners() {
    // moved add to cart to global-cart.js file

    // not sure how to do this with vanilla js since elements are dynamically created?

    // Title swatch carousel arrows — scroll the row of thumbnails
    $('.collection').on('click', '.js-title-swatches-prev, .js-title-swatches-next', (e) => {
      e.preventDefault();

      const arrow = e.currentTarget as HTMLElement;
      const track = arrow
        .closest('.title-swatches')
        ?.querySelector<HTMLElement>('.js-title-swatches-track');
      if (!track) return;

      const direction = arrow.classList.contains('js-title-swatches-next') ? 1 : -1;
      track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
    });

    // Title swatches — swap the card to the given sibling colourway in place.
    // Used by both click and hover (hovering commits the swap for good; the
    // card stays on the hovered colourway after the mouse leaves).
    const commitTitleSwatch = (swatch: HTMLElement) => {
      const card = swatch.closest<HTMLElement>('.js-product');
      if (!card || swatch.classList.contains('title-swatches__swatch--selected')) return;

      card.querySelectorAll('.js-title-swatch').forEach((s) =>
        s.classList.remove('title-swatches__swatch--selected'));
      swatch.classList.add('title-swatches__swatch--selected');

      // Swap the image for the sibling's primary image (drop hover/secondary UI)
      const imagesLink = card.querySelector<HTMLAnchorElement>('.product-card__images');
      if (imagesLink) {
        imagesLink.href = swatch.dataset.url || '#';
        imagesLink.innerHTML = `<img class="product-card__img product-card__img--primary"
          src="${swatch.dataset.image}" srcset="${swatch.dataset.srcset}" />`;
      }
      card.classList.remove('product-card--multi-image', 'product-card--lifestyle-image');
      card.querySelectorAll('.product-card__img-button').forEach((btn) => btn.remove());

      // The previous product's colour/material variant dots no longer apply
      card.querySelector('.variant-selectors--mini')?.remove();

      // Title link
      const titleLink = card.querySelector<HTMLAnchorElement>('.product-card__title a');
      if (titleLink) titleLink.href = swatch.dataset.url || '#';

      // Price
      const price = card.querySelector('.product-card__price');
      if (price) {
        price.outerHTML = buildPriceMarkup({
          hidePrice: swatch.dataset.hidePrice === 'true',
          price: parseFloat(swatch.dataset.price || '0'),
          compareAtPrice: swatch.dataset.compareAtPrice
            ? parseFloat(swatch.dataset.compareAtPrice)
            : null,
        });
      }

      // Add to cart
      const cartBtn = card.querySelector<HTMLButtonElement>('.js-add-to-cart');
      if (cartBtn) {
        const cartAvailable = swatch.dataset.available === 'true';
        cartBtn.dataset.id = `gid://shopify/ProductVariant/${swatch.dataset.objectId}`;
        cartBtn.classList.toggle('hidden', !cartAvailable);
        cartBtn.disabled = !cartAvailable;
      }

      // Keep the analytics attributes in sync
      card.dataset.product = swatch.dataset.handle || '';
      card.dataset.objectId = swatch.dataset.objectId || '';
    };

    $('.collection').on('click', '.js-title-swatch', (e) => {
      e.preventDefault();
      commitTitleSwatch(e.currentTarget as HTMLElement);
    });

    // Hovering a swatch commits the swap too (mouse only) — the card stays
    // on the hovered colourway even after the mouse leaves
    $('.collection').on('mouseenter', '.js-title-swatch', (e) => {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      commitTitleSwatch(e.currentTarget as HTMLElement);
    });

    $('.collection').on('mouseover', '.js-product', (e) => {
      if (window.matchMedia('(pointer: fine)').matches) {
        const $card = e.currentTarget;
        $card.classList.add('hover');
      }
    });

    $('.collection').on('mouseout', '.js-product', (e) => {
      if (window.matchMedia('(pointer: fine)').matches) {
        const $card = e.currentTarget;
        $card.classList.remove('hover');
      }
    });

    $('.collection').on(
      'focusin',
      '.js-product input, .js-product a, .js-product button',
      (e) => {
        if (window.matchMedia('(pointer: fine)').matches) {
          const $card = e.currentTarget.closest('.js-product');

          if (!$card.classList.contains('hover')) {
            document
              .querySelector('.js-product.hover')
              ?.classList.remove('hover');
            $card.classList.add('hover');
          }
        }
      }
    );

    $('.collection').on(
      'blur',
      '.js-product input, .js-product a, .js-product button',
      (e) => {
        if (window.matchMedia('(pointer: fine)').matches) {
          const $card = e.currentTarget.closest('.js-product');
          const allInputs = $card.querySelectorAll('input,a,button');

          // if we're losing focus on the last element, aka tabbing into the next div
          if (e.currentTarget === allInputs[allInputs.length - 1]) {
            $card.classList.remove('hover');
          }

          // if we're losing focus on the first element and the new target isn't in the same div,
          // we're tabbing backwards
          if (!$card.contains(e.relatedTarget)) {
            $card.classList.remove('hover');
          }
        }
      }
    );

    $('.collection').on('click', '.js-prev-img', (e) => {
      e.preventDefault();
      const card = e.currentTarget.closest('.js-product');
      mobileFirstImage(card);
    });

    $('.collection').on('click', '.js-next-img', (e) => {
      e.preventDefault();
      const card = e.currentTarget.closest('.js-product');
      mobileSecondaryImage(card);
    });

    function mobileFirstImage(card) {
      card.classList.remove('hover');
      card.querySelector('.js-prev-img').disabled = true;
      card.querySelector('.js-next-img').disabled = false;
    }

    function mobileSecondaryImage(card) {
      card.classList.add('hover');
      card.querySelector('.js-prev-img').disabled = false;
      card.querySelector('.js-next-img').disabled = true;
    }

    // swiping on mobile

    let touchStartX = 0;
    let touchEndX = 0;
    const swipeThreshold = 50; // Adjust this value as needed

    $('.collection').on(
      'touchstart',
      '.js-product:not(.disable-hover)',
      function(e) {
        touchStartX = e?.originalEvent?.touches[0].clientX ?? 0;
      }
    );

    $('.collection').on(
      'touchend',
      '.js-product:not(.disable-hover)',
      function(e) {
        touchEndX = e?.originalEvent?.changedTouches[0].clientX ?? 0;

        const deltaX = touchEndX - touchStartX;
        const card = e.currentTarget.closest('.js-product');

        if (deltaX > swipeThreshold) {
          // Right swipe
          mobileFirstImage(card);
        } else if (deltaX < -swipeThreshold) {
          // Left swipe
          mobileSecondaryImage(card);
        }
      }
    );

    $('.collection, .search-results__product-grid, .product-list').on(
      'click',
      '.js-mini-variant-selector',
      (e) => {
        let input = e.target;

        let id = input.dataset.variant;
        let imageSet = input.dataset.imageset;
        let price = input.dataset.price;
        let compareAtPrice = input.dataset.compareAtPrice;
        let handle = input.dataset.product;
        let available = input.classList.contains('disabled') ? false : true;

        let product = document.querySelector(
          `.js-product[data-product="${handle}"]`
        );

        if (product) {
          let addToCart = product.querySelector<HTMLInputElement>('.js-add-to-cart');

          let links = product.querySelectorAll('a');

          links.forEach((link) => {
            let url = new URL(link.href);
            let params = url.searchParams;

            params.set('v', extractID(id));

            url.search = params.toString();

            link.href = url.toString();
          });

          if (addToCart) {
            // update id on cart button
            addToCart.dataset.id = id;

            if (available) {
              addToCart.classList.remove('hidden');
              addToCart.disabled = false;
            } else {
              addToCart.classList.add('hidden');
              addToCart.disabled = true;
            }
          }

          if (imageSet) {
            // update image
            const imgEl = product.querySelector('.product-card__img--primary') as HTMLImageElement;
            imgEl.srcset = imageSet;
            product.classList.add('disable-hover');
          }
          const priceEl = product.querySelector('.js-product-price');
          if (priceEl) {
            let priceMarkup = '';
            if (compareAtPrice && price !== compareAtPrice) {
              priceMarkup += `<strike>${compareAtPrice}</strike>`;
            }
            priceMarkup += `<span>${price}</span>`;
            priceEl.innerHTML = priceMarkup;
          }
        }
      }
    );

    $('.collection').on('click', '.js-product', (e) => {
      if (window.fbq) {
        window.fbq('trackCustom', 'SelectContent', {
          product: e.currentTarget.getAttribute('data-product'),
        });
      }

      // TODO: There's probably a better way to grab the index name, but this works for now
      const indexName = document.querySelector<HTMLElement>('.sortBy__button--selected')?.dataset.value;

      const insightsData = {
        userToken: aa('getUserToken')?.toString(),
        eventName: 'Product Clicked',
        index: indexName ?? DEFAULT_INDEX,
        objectIDs: [e.currentTarget.getAttribute('data-object-id')],
        positions: [parseInt(e.currentTarget.getAttribute('data-position'), 10)],
        queryID: e.currentTarget.getAttribute('data-query-id'),
      };

      aa('clickedObjectIDsAfterSearch', insightsData);

      // Dual-write to GA4 so we own the query→click data (see search-events.ts)
      trackSearchEvent('algolia_result_click', {
        object_id: e.currentTarget.getAttribute('data-object-id'),
        query_id: e.currentTarget.getAttribute('data-query-id'),
        list_position: parseInt(e.currentTarget.getAttribute('data-position'), 10),
        surface: 'collection',
      });
    });
  }
};

const customHits = connectHits(renderHits);

/**
 * Editorial collections: infinite (accumulating) hits with a "Show more"
 * button, "Showing X of Y" line and progress track instead of pagination.
 * Page 0 / refinement changes re-render fully (renderHits); subsequent pages
 * APPEND only the new cards so the grid never collapses and the viewport
 * doesn't jump while cards build.
 */
let editorialShowMore: (() => void) | null = null;
let editorialRenderedCount = 0;
let editorialQuerySig = '';

/** Signature of the current query/refinements (page-independent) so we can
 * tell a genuine refinement change (rebuild grid) from a stale re-fire of the
 * same query while a further page loads (keep the grid, ignore).
 * MUST include the index name: changing the Sort By option switches the active
 * replica index (e.g. shopify_products -> shopify_products_price_desc) but
 * leaves `results.params` (query + filters, minus page) identical. Without the
 * index prefix a sort change reads as "same query" and the grid never rebuilds
 * with the re-sorted hits. */
const editorialSignature = (results: any): string => {
  const params = String(results?.params ?? `${results?.query ?? ''}`)
    .replace(/[?&](page|offset|length|hitsPerPage)=[^&]*/g, '');
  return `${results?.index ?? ''}|${params}`;
};

const updateEditorialLoadmoreUI = (hitsCount: number, total: number, isLastPage: boolean, loading = false) => {
  const area = document.getElementById('js-editorial-loadmore');
  if (!area) return;
  area.hidden = !total;
  const shown = document.getElementById('js-editorial-shown');
  if (shown) shown.textContent = t('algolia:pagination.showingXofY', { from: 1, to: hitsCount, total });
  const progress = document.getElementById('js-editorial-progress');
  if (progress) progress.style.width = total ? `${Math.min(100, (100 * hitsCount) / total)}%` : '0%';
  const btn = document.getElementById('js-editorial-more') as HTMLButtonElement | null;
  if (btn) {
    btn.hidden = !!isLastPage;
    btn.disabled = loading;
    btn.textContent = loading ? t('general:loadingMore') : t(isJewelleryCatalogue() ? 'general:showMorePieces' : 'general:showMoreWatches');
  }
};

const renderEditorialInfiniteHits = (renderOptions: any, isFirstRender: boolean) => {
  const { results, hits, showMore, isLastPage, widgetParams } = renderOptions;
  editorialShowMore = showMore;

  if (isFirstRender) {
    renderHits(renderOptions, isFirstRender);
    document.getElementById('js-editorial-more')?.addEventListener('click', () => {
      updateEditorialLoadmoreUI(editorialRenderedCount, results?.nbHits ?? 0, false, true);
      editorialShowMore && editorialShowMore();
    });
    return;
  }

  const total = results?.nbHits ?? 0;
  const container = typeof widgetParams.container === 'string'
    ? document.querySelector<HTMLElement>(widgetParams.container)
    : widgetParams.container;
  const grid = container?.querySelector('ol.collection__grid');
  const sig = editorialSignature(results);
  const sameQuery = sig === editorialQuerySig;

  // Same query as what's already on screen:
  if (grid && sameQuery) {
    if (hits.length > editorialRenderedCount) {
      // a further page arrived — APPEND its cards, leave the grid untouched
      const newHits = hits.slice(editorialRenderedCount);
      editorialRenderedCount = hits.length;
      updateEditorialLoadmoreUI(hits.length, total, !!isLastPage, true);
      (async () => {
        const temp = document.createElement('div');
        latestCallTimestamp = new Date().getTime();
        await loadHitsAsync(newHits, temp, latestCallTimestamp, results);
        const items = temp.querySelectorAll('.collection__grid-item');
        if (items.length && grid) {
          const frag = document.createDocumentFragment();
          items.forEach((li) => frag.appendChild(li));
          grid.appendChild(frag);
          initAtelierRails(container);
          initSignatureCardPreviews();
          initCollectionAffirm();
          // measure overflow only on the newly added swatches, not the whole
          // (growing) grid — avoids O(n²) forced reflows as pages accumulate
          items.forEach((li) => {
            const el = li.querySelector<HTMLElement>('.title-swatches');
            const track = el?.querySelector<HTMLElement>('.js-title-swatches-track');
            if (el && track) el.classList.toggle('title-swatches--scrollable', track.scrollWidth > track.clientWidth + 1);
          });
        }
        updateEditorialLoadmoreUI(hits.length, total, !!isLastPage, false);
      })();
    }
    // else: stale re-fire of the same query (e.g. cached page-0 render while
    // the next page loads) — do nothing, never collapse the grid
    return;
  }

  // New query / refinement change (or first render) — rebuild the grid fully
  editorialQuerySig = sig;
  editorialRenderedCount = hits.length;
  renderHits(renderOptions, isFirstRender);
  updateEditorialLoadmoreUI(hits.length, total, !!isLastPage, false);
};
const customEditorialInfiniteHits = connectInfiniteHits(renderEditorialInfiniteHits);

/** Editorial applied-filter chips (price ranges merged into one labelled chip) */
const renderEditorialChips = (renderOptions: any) => {
  const { items, widgetParams } = renderOptions;
  const container = document.querySelector<HTMLElement>(widgetParams.container);
  if (!container) return;

  const actions: Array<() => void> = [];
  const chip = (label: string) => {
    const index = actions.length - 1;
    return `<button type="button" class="collection-editorial-chip" data-chip="${index}">${label}<span class="collection-editorial-chip__x" aria-hidden="true">×</span></button>`;
  };
  const chips: string[] = [];
  (items || []).forEach((item: any) => {
    if (widgetParams.stockFilterEnabled && item.attribute === IN_STOCK_ATTRIBUTE && item.refinements.length) {
      actions.push(() => item.refinements.forEach((r: any) => item.refine(r)));
      chips.push(chip(t('algolia:filters.inStock.label')));
    } else if (item.attribute === 'price' && item.refinements.length) {
      const values = item.refinements.map((r: any) => Number(r.value)).sort((a: number, b: number) => a - b);
      const label = item.refinements.length === 1
        ? `${item.refinements[0].operator === '<=' ? t('general:under') : t('general:over')} ${formatAsMoney(values[0], getPageLang())}`
        : `${formatAsMoney(values[0], getPageLang())} – ${formatAsMoney(values[values.length - 1], getPageLang())}`;
      actions.push(() => item.refinements.forEach((r: any) => item.refine(r)));
      chips.push(chip(label));
    } else {
      item.refinements.forEach((r: any) => {
        actions.push(() => item.refine(r));
        const label = item.attribute === 'named_tags.available' && r.value === 'online'
          ? t('algolia:filters.availableOnline.label') : r.label;
        chips.push(chip(label));
      });
    }
  });
  container.innerHTML = chips.join('');
  container.querySelectorAll<HTMLButtonElement>('button[data-chip]').forEach((btn) => {
    btn.addEventListener('click', () => actions[Number(btn.dataset.chip)]?.());
  });
};
const customEditorialChips = connectCurrentRefinements(renderEditorialChips);

/** Editorial search box (submit-to-search, mockup styling) with an
 * autocomplete dropdown constrained to the current collection's products */
let editorialSearchRefine: ((value: string) => void) | null = null;
const renderEditorialSearch = (renderOptions: any, isFirstRender: boolean) => {
  const { query, refine, widgetParams } = renderOptions;
  const container = document.querySelector<HTMLElement>(widgetParams.container);
  if (!container) return;
  editorialSearchRefine = refine;

  if (isFirstRender) {
    container.innerHTML = `
      <form class="collection-editorial-search" role="search" novalidate>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></svg>
        <input type="search" class="collection-editorial-search__input" placeholder="${t('general:searchPlaceholder')}" autocomplete="off" aria-label="${t('general:searchPlaceholder')}" />
        <button type="submit" class="collection-editorial-search__submit">${t('general:searchAction')}</button>
      </form>
      <div class="collection-editorial-suggest" hidden></div>`;
    const form = container.querySelector('form') as HTMLFormElement;
    const input = container.querySelector('input') as HTMLInputElement;
    const dropdown = container.querySelector('.collection-editorial-suggest') as HTMLElement;

    const hideSuggestions = () => { dropdown.hidden = true; };

    // Autocomplete: same index as the grid, constrained to this collection
    const collectionHandle = document.querySelector<HTMLElement>('.collection-page')?.dataset.collection;
    let suggestTimer: ReturnType<typeof setTimeout> | undefined;
    let suggestSeq = 0;
    const suggest = async () => {
      const term = input.value.trim();
      if (term.length < 2 || !collectionHandle) { hideSuggestions(); return; }
      const seq = ++suggestSeq;
      try {
        const { results } = await searchClient.search([{
          indexName: DEFAULT_INDEX,
          query: term,
          params: {
            hitsPerPage: 8,
            filters: `collections:${collectionHandle} AND NOT tags:"inquiry-only"`,
            attributesToRetrieve: ['title', 'handle', 'image', 'product_image', 'named_tags', 'vendor', 'sku'],
            attributesToHighlight: ['sku'],
            analytics: false,
            clickAnalytics: false,
          },
        }] as any);
        if (seq !== suggestSeq) return; // stale response
        const hits = (results?.[0] as any)?.hits || [];
        if (!hits.length || document.activeElement !== input) { hideSuggestions(); return; }
        dropdown.innerHTML = hits.map((hit: any) => {
          const url = stardust.route(`${getPageLang()}.shopify.slug`, { slug: hit.handle });
          const img = getSizedShopifyImageUrl(hit.product_image || hit.image, 96);
          const brand = (hit.named_tags && hit.named_tags.brand) || hit.vendor || '';
          let title = String(hit.title || '');
          if (brand && title.toLowerCase().startsWith(brand.toLowerCase())) title = title.slice(brand.length).trim();
          // SKU under the title, with the matched part highlighted (like the
          // main search autocomplete)
          const skuHtml = (hit._highlightResult && hit._highlightResult.sku && hit._highlightResult.sku.value) || hit.sku || '';
          return `<a class="collection-editorial-suggest__item" href="${url}">
            ${img ? `<img src="${img}" alt="" loading="lazy" />` : '<span class="collection-editorial-suggest__no-img"></span>'}
            <span class="collection-editorial-suggest__text">
              <span><strong>${brand}</strong> ${title}</span>
              ${skuHtml ? `<span class="collection-editorial-suggest__sku">${skuHtml}</span>` : ''}
            </span>
          </a>`;
        }).join('');
        dropdown.hidden = false;
      } catch {
        hideSuggestions();
      }
    };

    input.addEventListener('input', () => {
      if (suggestTimer) clearTimeout(suggestTimer);
      suggestTimer = setTimeout(suggest, 200);
    });
    input.addEventListener('focus', () => { if (input.value.trim().length >= 2) suggest(); });
    input.addEventListener('keydown', (event) => { if (event.key === 'Escape') hideSuggestions(); });
    // delay so a click on a suggestion can land first
    input.addEventListener('blur', () => setTimeout(hideSuggestions, 200));

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      // Enter keeps the input focused: cancel queued work and invalidate any
      // in-flight suggestions so they cannot reopen over the sorted results.
      if (suggestTimer) clearTimeout(suggestTimer);
      suggestTimer = undefined;
      suggestSeq += 1;
      hideSuggestions();
      editorialSearchRefine && editorialSearchRefine(input.value.trim());
    });
    // clearing the field (native ×) clears the query
    input.addEventListener('search', () => {
      if (!input.value && editorialSearchRefine) { hideSuggestions(); editorialSearchRefine(''); }
    });
    return;
  }

  const input = container.querySelector<HTMLInputElement>('input');
  if (input && document.activeElement !== input && input.value !== query) input.value = query;
};
const customEditorialSearch = connectSearchBox(renderEditorialSearch);

const isEditorialCollection = () => !!document.querySelector('.collection-page--editorial');

/** Keep existing watch routing; add query/sort state only to these shopping catalogues. */
const preservesCatalogueQuery = (pathname: string, watch: boolean, jewellery: boolean): boolean => {
  if (watch) return true;
  const path = pathname.replace(/^\/fr(?=\/|$)/, '').replace(/\/$/, '');
  if (/(?:^|[/-])(?:rolex|tag-?heuer)(?:[/-]|$)/i.test(path)) return false;
  return (jewellery && /^\/collections\/fine-jewellery(?:\/[a-z0-9-]+)*$/i.test(path))
    || /^\/collections\/gifts-accessories(?:\/[a-z0-9-]+)*$/i.test(path);
};

// Create the render function
const renderSortBy = (renderOptions, isFirstRender: boolean) => {
  const {
    options,
    currentRefinement,
    refine,
    widgetParams,
    // `canRefine` is only available from v4.45.0
    // Use `hasNoResults` in earlier minor versions.
  } = renderOptions;

  const selectedOption = options.find((option) => {
    return option.value === currentRefinement;
  });

  // TODO: not sure what isFirstRender implies, but all event listeners have to be in first
  if (isFirstRender) {
    widgetParams.container.classList.add('sortBy');
    // const header = document.createElement('div');
    widgetParams.container.innerHTML =
      `<span class="sortBy__label">${t('general:sortBy')}:</span>`;

    const toggle = document.createElement('button');
    toggle.classList.add('sortBy__toggle', 'js-sortBy');

    const toggleText = `${selectedOption.label}`;

    toggle.innerHTML = toggleText;

    widgetParams.container.appendChild(toggle);

    const list = document.createElement('ul');
    list.setAttribute('aria-hidden', 'true');
    list.classList.add('sortBy__list', 'js-sortBy-list');

    widgetParams.container.appendChild(list);

    options.forEach((option) => {
      const button = document.createElement('button');
      button.dataset.value = option.value;
      button.classList.add('sortBy__button');

      button.innerHTML = `<span>${option.label}</span>`;

      if (option.value === currentRefinement) {
        button.classList.add('sortBy__button--selected');
      }

      button.addEventListener('click', (event) => {
        event.preventDefault();
        let oldBtn = widgetParams.container.querySelector(
          '.sortBy__button--selected'
        );

        oldBtn.classList.remove('sortBy__button--selected');

        let btn = event.currentTarget as HTMLElement;

        btn.classList.add('sortBy__button--selected');

        let toggles = document.querySelectorAll('.sortBy__toggle');

        toggles.forEach((toggle) => {
          toggle.innerHTML = btn.innerHTML;
        });

        refine(btn!.dataset.value);

        // close dropdown
        toggle.classList.remove('sortBy__toggle--open');
        list.setAttribute('aria-hidden', 'true');
      });

      const li = document.createElement('li');
      li.className = 'sortBy__list-item';

      li.append(button);

      list.append(li);
    });

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      if (toggle.classList.contains('sortBy__toggle--open')) {
        toggle.classList.remove('sortBy__toggle--open');
        list.setAttribute('aria-hidden', 'true');
      } else {
        toggle.classList.add('sortBy__toggle--open');
        list.removeAttribute('aria-hidden');
      }
    });
  }

  // History restores the connector's real index without clicking a sort button.
  // Keep the ready native-watch control in sync without rebuilding focused UI.
  if (!isFirstRender && watchRecency && selectedOption) {
    widgetParams.container.querySelectorAll('.sortBy__button').forEach((button: HTMLElement) => {
      button.classList.toggle('sortBy__button--selected', button.dataset.value === currentRefinement);
    });
    widgetParams.container.querySelectorAll('.sortBy__toggle').forEach((toggle: HTMLElement) => {
      if (toggle.textContent !== selectedOption.label) toggle.textContent = selectedOption.label;
    });
  }
};

const customSortBy = connectSortBy(renderSortBy);

// @ts-ignore
function init(searchClient: SearchClient) {
  const mainElem = document.querySelector('main[data-collection]') as HTMLElement;
  const watchBuyingCollection = mainElem.dataset.watchBuying === 'collection';
  const stockFilterEnabled = stockFilterEnabledForPage(document, window.location);
  const jewelleryBuyingCollection = mainElem.dataset.jewelleryBuying === 'collection';
  const preserveCatalogueState = preservesCatalogueQuery(window.location.pathname, watchBuyingCollection, jewelleryBuyingCollection);
  const collectionHandle = mainElem.dataset.collection ?? 'default';
  // Bestsellers is a virtual collection — no Shopify collection backs it.
  // It runs on the PRIMARY index, whose empty-query ranking is availability
  // then desc(popularity) (in-store + online units, algolia:sync-popularity),
  // and filters by popularity instead of collection membership. The featured
  // replica would pollute that order with named_tags.sortOrder.
  const isBestsellers = collectionHandle === 'bestsellers';
  const pageIndex = isBestsellers ? getIndexName(getPageLang()) : DEFAULT_INDEX;
  const collectionClass = [...mainElem.classList].filter((c) => c.includes('collection-page--'))[0];
  // console.debug({ collectionClass });

  document.addEventListener('DOMContentLoaded', async () => {
    // Load the namespaces we need
    t = await i18nextInstance;
    await i18next.loadNamespaces(document.querySelector('[data-jewellery-buying="collection"]') ? ['general', 'algolia', 'jewelleryTryOn'] : ['general', 'algolia']);

    // We need to load the filters after we have t
    const additionalFilters = getFilterObjects(collectionHandle, getPageLang(), t, collectionClass);
    const filterSetIdentifier = getFilterSetIdentifier(collectionHandle, getPageLang(), t, collectionClass);
    if (stockFilterEnabled) {
      // One owner for inventory_quantity: this checkbox replaces Any location.
      // Store location radios remain independent and combine with stock using AND.
      const stockFilter = new Filter({
        title: t('algolia:filters.inStock.label'),
        attribute: IN_STOCK_ATTRIBUTE,
        id: 'inStock',
        widgetType: 'numericMenu',
        generateWidget: function() {
          return inStockNumericMenu({ container: '#inStock', label: this.title });
        },
      });
      const onlinePosition = additionalFilters.findIndex(filter => filter.id === 'availableOnline');
      additionalFilters.splice(onlinePosition < 0 ? additionalFilters.length : onlinePosition, 0, stockFilter);
      const oldAnyLocation = document.getElementById('filters-location-all-locations');
      if (oldAnyLocation) oldAnyLocation.hidden = true;
    }
    const watchSortByValues = preserveCatalogueState
      ? watchRecencySortOptions(buildSortByOptions(t, getPageLang()), watchRecency, getPageLang(), t('algolia:sortBy.watchNewest')).map((item) => item.value)
      : [];
    const watchOtherSortByValues = preserveCatalogueState
      ? watchRecencySortOptions(buildSortByOptions(t, getPageLang() === 'fr' ? 'en' : 'fr'), watchRecency, getPageLang() === 'fr' ? 'en' : 'fr', t('algolia:sortBy.watchNewest')).map((item) => item.value)
      : [];

    // Add the DOM elements for the filters to the page
    const filtersAccordion = document.querySelector('.filters__accordion') as HTMLUListElement;
    additionalFilters.forEach((filter) => {
      // console.debug(filter);
      const li = document.createElement('li');
      li.classList.add('filters__accordion-item', 'js-accordion-item');
      // Reliable label/id for the mobile filter pill bar (filters.ts) — available
      // immediately, before Algolia renders the widget (toggles have no title in the DOM).
      li.dataset.pillLabel = filter.title;
      li.dataset.sectionId = filter.id;
      // These filters should be expanded by default (filters.ts opens them once rendered).
      // Jewellery pages open Brand + Jewellery Type (with the always-open price
      // slider that makes the desired "first three expanded").
      if (
        filter.id === 'brand'
        || filter.id === 'gender'
        || (JEWELLERY_TYPE_OPEN_SETS.includes(filterSetIdentifier) && filter.id === 'category')
        || (STYLE_OPEN_SETS.includes(filterSetIdentifier) && filter.id === 'style')
      ) {
        li.dataset.accordionOpen = 'true';
      }

      const div = document.createElement('div');
      div.id = filter.id;

      if (filter.widgetType === 'refinementList') {
        div.classList.add('filters__checklist');
        li.classList.add('filters__accordion-item--instantSearchPanel');
      } else if (filter.widgetType === 'toggle' || filter.id === 'inStock') {
        div.classList.add('filters__toggle');
      } else if (filter.widgetType === 'range') {
        div.classList.add('filters__range');
        li.classList.add('filters__accordion-item--instantSearchPanel');
      }

      li.appendChild(div);
      filtersAccordion.appendChild(li);
    });

    // Timepieces + jewellery: the static "Available Location" item is authored
    // first in the template, but the desired order puts it last — move it to
    // the end of the accordion. On jewellery pages the static "Browse By
    // Category" child-collection links go last too (after the curated list).
    if (filterSetIdentifier === 'timepieces' || JEWELLERY_FILTER_SETS.includes(filterSetIdentifier)) {
      if (JEWELLERY_FILTER_SETS.includes(filterSetIdentifier)) {
        const childrenItem = document
          .querySelector('.filters__checklist--links')
          ?.closest('.filters__accordion-item');
        if (childrenItem) {
          filtersAccordion.appendChild(childrenItem);
        }
      }
      const locationItem = document
        .querySelector('#filters-location-all-locations')
        ?.closest('.filters__accordion-item');
      if (locationItem) {
        filtersAccordion.appendChild(locationItem);
      }
    }

    // And we have to instantiate the instantsearch after we have the filters, since it uses them in the stateMapping
    const search = instantsearch({
      searchClient,
      indexName: pageIndex,
      insights: true,
      routing: {
        router: history(),
        stateMapping: {
          // @ts-ignore
          stateToRoute(uiState) {
            // console.debug('stateToRoute', uiState);

            const indexUiState = uiState[pageIndex];
            // This is the format used below in routeToState
            let refinements = {};

            additionalFilters.forEach((filter: any) => {
              const value = indexUiState[filter.widgetType]?.[filter.attribute];
              refinements[filter.id] = filter.id === 'inStock'
                ? (inStockRouteEnabled(value) ? true : undefined) : value;
            });

            // Price widget is declared separately from the rest of the filters
            if (indexUiState['range']?.price) {
              refinements['price'] = indexUiState['range'].price;
            }

            const numericMenuFilters = indexUiState['numericMenu'];
            if (numericMenuFilters) {
              // Add price filter if present
              if (numericMenuFilters['price']) {
                refinements['price'] = numericMenuFilters['price'];
              }

              // Add any specific location filters if present
              const locationInventoryFilters = Object.entries(numericMenuFilters).filter(([key]) => {
                return key.startsWith('locations_inventory.');
              });
              locationInventoryFilters.forEach(([key, value]) => {
                refinements[key] = value;
              });

              // Add "any location" filter if present
              if (!stockFilterEnabled && numericMenuFilters['inventory_quantity']) {
                refinements['inventory_quantity'] = numericMenuFilters['inventory_quantity'];
              }
            }

            return {
              ...refinements,
              page: indexUiState.page,
              // Keep the submitted query and selected replica alongside the
              // filters when sorting or restoring a shopping catalogue.
              ...(preserveCatalogueState ? {
                query: indexUiState.query,
                sortBy: indexUiState.sortBy,
              } : {}),
            };
          },
          // @ts-ignore
          routeToState(routeState) {
            // console.debug('routeToState', routeState);

            let refinements = {};
            // Website language switching retains the query string. Translate
            // only an offered EN/FR sort index to its matching current option.
            const requestedWatchSort: unknown = watchRecencyRouteSort(routeState, watchRecency);
            const watchSortBy = typeof requestedWatchSort === 'string'
              ? (watchSortByValues.includes(requestedWatchSort)
                ? requestedWatchSort
                : watchSortByValues[watchOtherSortByValues.indexOf(requestedWatchSort)])
              : undefined;

            additionalFilters.forEach((filter: any) => {
              if (!refinements[filter.widgetType]) {
                refinements[filter.widgetType] = {};
              }
              const routeValue: unknown = filter.id === 'inStock'
                ? inStockRouteValue(routeState) : routeState[filter.id];
              const localeFacet = watchBuyingCollection && ['gender', 'caseSize', 'waterResistance', 'movement'].includes(filter.id);
              const localizedValue = localeFacet ? nativeWatchFacetValue(filter.id, routeValue, getPageLang()) : routeValue;
              // Old/shared links can encode a single facet without array brackets.
              refinements[filter.widgetType][filter.attribute] = localeFacet && typeof localizedValue === 'string'
                ? [localizedValue] : localizedValue;
            });

            // Price widget is declared separately from the rest of the filters
            if (routeState.price) {
              refinements['range'] = {
                ...refinements['range'],
                price: routeState.price,
              };

              refinements['numericMenu'] = {
                ...refinements['numericMenu'],
                price: routeState.price,
              };
            }

            // A location-only catalogue selection has no price widget state yet.
            // Initialize the container before restoring availability filters.
            if (preserveCatalogueState) {
              refinements['numericMenu'] = { ...refinements['numericMenu'] };
            }

            // Add any specific location filters if present
            const locationInventoryFilters = Object.entries(routeState).filter(([key]) => {
              return key.startsWith('locations_inventory.');
            });
            locationInventoryFilters.forEach(([key, value]) => {
              refinements['numericMenu'][key] = value;
            });

            // Add "any location" filter if present
            if (!stockFilterEnabled && routeState['inventory_quantity']) {
              refinements['numericMenu']['inventory_quantity'] = routeState['inventory_quantity'];
            }

            return {
              [pageIndex]: {
                // These basically have a key for each widget type, with keys for each attribute
                // e.g. { refinementList: { 'named_tags.collection': ['collection'] } }
                page: routeState.page,
                ...(preserveCatalogueState ? {
                  query: typeof routeState.query === 'string' ? routeState.query : '',
                  ...(watchSortBy ? { sortBy: watchSortBy } : {}),
                } : {}),
                ...refinements,
              },
            };
          },
        },
      },
    });

    // Trigger Facebook Pixel tracking for the collection page
    if (window.fbq) {
      window.fbq('trackCustom', 'ViewItemList', {
        collection: collectionHandle,
      });
    }

    const widgets: any = [
      // This replaces with a filter for "all locations" that shows total inventory across all locations
      ...(stockFilterEnabled ? [] : [locationAvailabilityNumericMenu({
        container: '#filters-location-all-locations',
        label: t('algolia:filters.availableLocation.options.ANY_LOCATION'),
        attribute: 'inventory_quantity',
      })]),
      // Individual locations
      // These IDs are the Shopify location IDs, which are in the URL when you edit the location in Shopify
      locationAvailabilityNumericMenu({
        container: '#filters-location-cambridge',
        label: t('algolia:filters.availableLocation.options.FLAGSHIP_BOUTIQUE'),
        locationId: 78254014757,
      }),
      locationAvailabilityNumericMenu({
        container: '#filters-location-waterloo',
        label: t('algolia:filters.availableLocation.options.CONESTOGA_MALL'),
        locationId: 83272761637,
      }),
      clearRefinements({
        container: '#filters-location-clear',
        includedAttributes: [
          ...(stockFilterEnabled ? [] : ['inventory_quantity']),
          'locations_inventory.78254014757',
          'locations_inventory.83272761637',
          'locations_inventory.108549341477',
        ],
        templates: {
          // @ts-ignore
          resetLabel(params, { html }) {
            return html`<span>${t('algolia:filters.availableLocation.options.CLEAR')}</span>`;
          },
        },
        cssClasses: {
          // root: ['ais-ClearRefinements', 'ais-ClearRefinements--locations'],
          button: [
            'ais-ClearRefinements-button',
            'ais-ClearRefinements-button--radios',
          ],
        },
      }),
      ...(isEditorialCollection()
        ? [customEditorialInfiniteHits({
            // @ts-ignore
            container: document.querySelector('#hits'),
          })]
        : [customHits({
            // @ts-ignore (apparently the types haven't been added/updated for custom connectors yet?)
            container: document.querySelector('#hits'),
          })]),
      // Editorial catalogue: search box in the results heading
      ...(isEditorialCollection() && document.querySelector('#editorial-search-box')
        ? [customEditorialSearch({
            // @ts-ignore
            container: '#editorial-search-box',
          })]
        : []),
      // Editorial catalogue: applied filter chips + clear all
      ...(isEditorialCollection() && document.querySelector('#editorial-chips-list')
        ? [customEditorialChips({
            // @ts-ignore
            container: '#editorial-chips-list',
            stockFilterEnabled,
            excludedAttributes: ['query', ...(stockFilterEnabled ? [] : ['inventory_quantity']), 'locations_inventory.78254014757', 'locations_inventory.83272761637', 'locations_inventory.108549341477'],
          }),
          clearRefinements({
            container: '#editorial-chips-clear',
            excludedAttributes: ['query'],
            templates: {
              // @ts-ignore
              resetLabel(params, { html }) {
                return html`<span>${t('general:clearAll')}</span>`;
              },
            },
            cssClasses: { button: 'collection-editorial-chips__clear' },
          })]
        : []),
      // "(246)" total count beside the collection title (only if present)
      ...(document.querySelector('#js-collection-count')
        ? [stats({
            container: '#js-collection-count',
            templates: {
              text(data: any, { html }) {
                // An empty tagged template can yield undefined in the widget renderer.
                // Keep a concrete zero for enabled watch and jewellery catalogues.
                if (watchBuyingCollection || jewelleryBuyingCollection) return html`(${Number.isFinite(data.nbHits) ? data.nbHits : 0})`;
                return data.hasNoResults ? html`` : html`(${data.nbHits})`;
              },
            },
          })]
        : []),
      // "Showing 1 - 24" on a smaller line under the title (only if present)
      ...(document.querySelector('#js-collection-showing')
        ? [stats({
            container: '#js-collection-showing',
            templates: {
              text(data: any, { html }) {
                const { hitsPerPage, nbHits, nbPages, page, hasNoResults } = data;
                if (hasNoResults) return html``;
                const startNum = page * hitsPerPage + 1;
                const endNum = page + 1 < nbPages ? (page + 1) * hitsPerPage : nbHits;
                return html`${t('algolia:pagination.showingXtoY', {
                  from: startNum,
                  to: endNum,
                })}`;
              },
            },
          })]
        : []),
      stats({
        container: '#stats',
        templates: {
          text(data: any, { html }) {
            const { hitsPerPage, nbHits, nbPages, page, hasNoResults } = data;

            const nbPage = page + 1;

            if (hasNoResults) {
              return html`<span>${t('general:noResults')}</span>.`;
            } else {
              const startNum = page * hitsPerPage + 1;

              const endNum = nbPage < nbPages ? nbPage * hitsPerPage : nbHits;

              return html`<span>
              ${t('algolia:pagination.showingXofY', {
                from: startNum,
                to: endNum,
                total: nbHits,
              })}
              </span>`;
            }
          },
        },
      }),
      // The "sort by" in the filters sidebar
      customSortBy({
        // @ts-ignore (apparently the types haven't been added/updated for custom connectors yet?)
        container: document.querySelector('#filters-sortBy'),
        // On Bestsellers the page runs on the primary index, so the
        // "Featured" option must point there too (sortBy requires the
        // active index to be among its options; here Featured = best-selling)
        items: watchRecencySortOptions(buildSortByOptions(t, getPageLang()), watchRecency, getPageLang(), t('algolia:sortBy.watchNewest')).map((item) =>
          isBestsellers && item.value === DEFAULT_INDEX ? { ...item, value: pageIndex } : item,
        ),
        // Optional parameters
      }),
      // Price range slider, only initialize rangeSlider if #priceSlider exists
      ...(document.querySelector('#priceSlider')
        ? [
          rangeSlider({
            container: '#priceSlider',
            attribute: 'price',
            min: 0,
            pips: false,
            step: 100,
            tooltips: {
              format: (rawValue) => {
                return formatAsMoney(rawValue, getPageLang());
              },
            },
            cssClasses: {
              root: 'range-slider',
            },
          }),
        ]
        : []),
      ...(document.querySelector('#filters-byPrice') && document.querySelector('#priceFilterData')
        ? [
          (() => {
            // Get JSON from #priceFilterData
            const priceFilterDataElem = document.querySelector('#priceFilterData');
            let items: any[] = [];
            if (priceFilterDataElem) {
              try {
                const data = JSON.parse(priceFilterDataElem.textContent || '{}');
                items = Array.isArray(data) ? data : data.items || [];
              } catch (e) {
                console.error('Failed to parse #priceFilterData JSON', e);
              }
            }

            // Guard against mis-authored options: Algolia's numericMenu encodes an
            // item with neither `start` nor `end` to the empty value — identical to
            // the intended "All" item. A second such item collides with "All" and,
            // since it renders last, steals the default-selected state (showing e.g.
            // "OVER $4,500" selected with no filter applied). Keep only the first
            // item as the bounds-less "All"; drop any later option missing both
            // bounds until its start/end is set in Contentful.
            items = items.filter((item, index) => {
              if (index === 0) return true;
              const hasBound = item?.start != null || item?.end != null;
              if (!hasBound) {
                console.warn(
                  `[price filter] dropping option "${item?.label}" — it has no start/end and would collide with the "All" default.`,
                );
              }
              return hasBound;
            });

            return [
              numericMenu({
                container: '#filters-byPrice',
                attribute: 'price',
                items,
              }),
            ];
          })(),
        ].flat() // flatten because we return an array of widgets
        : []),
      ...(isEditorialCollection() ? [] : [pagination({
        container: '#pagination',
        templates: {
          first: t('algolia:pagination.first'),
          last: t('algolia:pagination.last'),
        },
        padding: 2,
        scrollTo: '.collection',
        cssClasses: {
          root: 'pagination',
          noRefinementRoot: 'pagination pagination--empty',
          list: 'pagination__list',
          item: 'pagination__item',
          firstPageItem: 'pagination__item--first',
          lastPageItem: 'pagination__item--last',
          previousPageItem: 'pagination__item--prev pagination__item--arrow',
          nextPageItem: 'pagination__item--next pagination__item--arrow',
          pageItem: 'pagination__item--page',
          selectedItem: 'pagination__item--selected',
          disabledItem: 'pagination__item--disabled',
          link: 'pagination__link',
        },
      })]),
      clearRefinements({
        container: '#clear-filters',
        templates: {
          // @ts-ignore
          resetLabel(params, { html }) {
            return html`<span>${t('algolia:resetFilters')}</span>`;
          },
        },
        cssClasses: {
          button: [
            'ais-ClearRefinements-button',
            'ais-ClearRefinements-button--reset-all',
          ],
        },
      }),
    ];

    // Add the collection to the configure widget if we have a collection page
    if (collectionHandle) {
      widgets.push(
        configure({
          // Thirty products fill complete rows at all native grid widths (5, 3, 2).
          ...(watchBuyingCollection || jewelleryBuyingCollection ? { hitsPerPage: 30 } : {}),
          ...(document.querySelector('[data-watch-buying="collection"]') ? { distinct: 0 } : {}),
          // Inquiry-only products (TAG Heuer) must never surface in collection
          // grids — smart collections auto-include new products, so this must
          // be enforced at query time, not by curation discipline
          filters: isBestsellers
            // Proven sellers only — popularity is total units (in-store +
            // online) written nightly by algolia:sync-popularity
            ? 'popularity >= 3 AND NOT tags:"inquiry-only"'
            : `collections:${collectionHandle} AND NOT tags:"inquiry-only"`,
          // High enough for the show-more refinement lists (collection/brand
          // facets can exceed 50 values — e.g. Tissot straps fitments)
          maxValuesPerFacet: 1000,
          // Personalization-lite: soft-boost this shopper's affinities
          // (empty array = no-op when disabled or no profile yet).
          // NOT on Bestsellers: optionalFilters score at the `filters`
          // ranking criterion, before customRanking, so they'd override the
          // true sales order — this page promises actual bestsellers.
          // NOT on editorial landings either: the same boost turns the
          // curated Featured order into a wall of one recently-viewed brand
          optionalFilters: (isBestsellers || isEditorialCollection()) ? [] : getOptionalFilters(),
        })
      );
    }

    // Add Tudor location availability filter if it's present on the page
    const tudorLocationFilterElem = document.querySelector('#filters-location-montreal');
    if (tudorLocationFilterElem) {
      widgets.push(
        locationAvailabilityNumericMenu({
          container: '#filters-location-montreal',
          label: t('algolia:filters.availableLocation.options.TUDOR_BOUTIQUE_MONTREAL'),
          locationId: 108549341477,
        }),
      );
    }

    // Add any additional filters for the collection
    widgets.push(
      ...additionalFilters.map((filter) => {
        // console.debug(filter);
        return filter.generateWidget();
      }),
    );

    search.addWidgets(widgets);
    if (watchBuyingCollection) {
      search.on('render', () => document.dispatchEvent(new CustomEvent('native-watch-filters-rendered')));
    }
    search.start();
    initializeTimepieceFilterJourney(search, t);

    // Editorial catalogue: dynamic heading/intro swap, Filters button (opens
    // the existing all-filters sheet) and active-filter count badge
    if (isEditorialCollection()) {
      const countBadge = document.getElementById('js-editorial-filter-count');
      search.on('render', () => {
        const ui: any = Object.values(search.getUiState())[0] || {};
        const listCount: number = ui.refinementList ? (Object.values(ui.refinementList) as any[]).reduce((n: number, v: any) => n + (Array.isArray(v) ? v.length : 1), 0) : 0;
        const numericCount = ui.numericMenu ? Object.keys(ui.numericMenu).length : 0;
        if (countBadge) {
          const n = listCount + numericCount;
          countBadge.textContent = String(n);
          countBadge.hidden = !n;
        }
        // "N watches" beside the Filters button (replaces the visible h1)
        const watchCount = document.getElementById('js-editorial-watch-count');
        const nbHits = (search.helper as any)?.lastResults?.nbHits;
        if (watchCount && typeof nbHits === 'number') {
          // Label = the page's Contentful Collection Name (falls back to a
          // generic pieces/watches noun when the field is empty)
          const countLabel = document.getElementById('collectionWrapper')?.dataset.collectionLabel
            || t(isJewelleryCatalogue() ? 'general:piecesLabel' : 'general:watchesLabel');
          watchCount.textContent = `${nbHits.toLocaleString(getPageLang() === 'fr' ? 'fr-CA' : 'en-CA')} ${countLabel}`;
        }
      });
      document.querySelector('.js-editorial-filters')?.addEventListener('click', () => {
        // the all-filters sheet presents as a centred lightbox on desktop
        document.querySelector<HTMLElement>('.filters-bar__pill--all')?.click();
      });
    }

    // Notify accordion module to bind newly-added accordion buttons
    // The widgets need to be instantiated or there's no trigger button
    document.dispatchEvent(new CustomEvent('accordion:add'));
  });
}

init(searchClient);

// Hook up filtering and smooth scroll on custom price filter buttons
document.addEventListener('DOMContentLoaded', () => {
  const priceFilterBtns = document.querySelectorAll<HTMLButtonElement>('.js-price-filter-btn');

  priceFilterBtns?.forEach((btn) => {
    btn.addEventListener('click', () => {
      // We have to use the label because instantsearch doesn't provide anything else
      const { label } = btn.dataset;

      const container = document.querySelector<HTMLElement>('#filters-byPrice');
      if (!container || !label) {
        return;
      }

      const match = Array.from(
        container.querySelectorAll<HTMLSpanElement>('.ais-NumericMenu-labelText')
      ).find((span) => span.textContent === label);

      if (match) {
        const input = match.closest('label')?.querySelector<HTMLInputElement>('input[type="radio"]');
        const deferred = input ? requestCollectionResultsScroll(!input.checked) : false;
        input?.click();
        if (!deferred) document.querySelector('#collectionWrapper')?.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });
});
